import { useRef, useLayoutEffect, useState } from 'react';
import type { ReactNode, CSSProperties, KeyboardEventHandler } from 'react';
import './Panel.css';

interface PanelProps {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}

/** Warm gradient card with border and shadow */
export function Panel({ children, className, style }: PanelProps) {
  return (
    <div className={`ui-panel warm-panel ${className ?? ''}`.trim()} style={style}>
      {children}
    </div>
  );
}

/** Full-screen layout with warm gradient background */
export function ScreenLayout({ children, className, style }: PanelProps) {
  return (
    <div className={`ui-screen ${className ?? ''}`.trim()} style={style}>
      <div className="menu-bg" />
      {children}
    </div>
  );
}

/** Horizontal divider line */
export function Divider({ className }: { className?: string }) {
  return <hr className={`ui-divider ${className ?? ''}`.trim()} />;
}

interface ChipProps {
  children: ReactNode;
  active?: boolean;
  onClick?: () => void;
  className?: string;
}

/** Small toggle chip/pill button */
export function Chip({ children, active, onClick, className }: ChipProps) {
  return (
    <button
      className={`ui-chip ${active ? 'active' : ''} ${className ?? ''}`.trim()}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

interface TextButtonProps {
  children: ReactNode;
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
  'aria-label'?: string;
}

/** Plain text button — no 3D styling, no hover animation. Just clickable text. */
export function TextButton({ children, onClick, className, disabled, 'aria-label': ariaLabel }: TextButtonProps) {
  return (
    <button
      className={`ui-text-btn ${className ?? ''}`.trim()}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
    >
      {children}
    </button>
  );
}

interface ToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  className?: string;
}

/** Toggle switch */
export function Toggle({ checked, onChange, label, className }: ToggleProps) {
  return (
    <button
      className={`ui-toggle ${checked ? 'active' : ''} ${className ?? ''}`.trim()}
      onClick={() => onChange(!checked)}
      role="switch"
      aria-checked={checked}
      aria-label={label}
    >
      <span className="ui-toggle-track">
        <span className="ui-toggle-knob" />
      </span>
      {label && <span className="ui-toggle-label">{label}</span>}
    </button>
  );
}

interface MultiToggleOption<T extends string | number = string | number> {
  value: T;
  label: string;
}

interface MultiToggleProps<T extends string | number = string | number> {
  options: MultiToggleOption<T>[];
  value: T;
  onChange: (value: T) => void;
  label?: string;
  className?: string;
  disabled?: boolean;
  compact?: boolean;
}

/** Segmented multi-toggle control for choosing between discrete options */
export function MultiToggle<T extends string | number = string | number>({
  options,
  value,
  onChange,
  label,
  className,
  disabled,
  compact,
}: MultiToggleProps<T>) {
  return (
    <div
      className={`ui-multi-toggle ${compact ? 'compact' : ''} ${disabled ? 'disabled' : ''} ${className ?? ''}`.trim()}
      role="radiogroup"
      aria-label={label}
    >
      {options.map((opt) => (
        <button
          key={String(opt.value)}
          className={`ui-multi-toggle-option ${opt.value === value ? 'active' : ''}`}
          role="radio"
          aria-checked={opt.value === value}
          disabled={disabled}
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

interface SegmentedToggleOption<T extends string | number = string | number> {
  value: T;
  label: ReactNode;
  'aria-label'?: string;
}

interface SegmentedToggleProps<T extends string | number = string | number> {
  options: SegmentedToggleOption<T>[];
  value: T;
  onChange: (value: T) => void;
  label?: string;
  className?: string;
  disabled?: boolean;
}

/** Sliding-thumb segmented toggle — supports any number of icon/text options */
export function SegmentedToggle<T extends string | number = string | number>({
  options,
  value,
  onChange,
  label,
  className,
  disabled,
}: SegmentedToggleProps<T>) {
  const activeIndex = options.findIndex((o) => o.value === value);
  const containerRef = useRef<HTMLDivElement>(null);
  const [thumb, setThumb] = useState<{ left: number; width: number } | null>(null);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || activeIndex < 0) { setThumb(null); return; }
    const buttons = container.querySelectorAll<HTMLElement>('.ui-seg-toggle__option');
    const btn = buttons[activeIndex];
    if (!btn) return;
    const cr = container.getBoundingClientRect();
    const br = btn.getBoundingClientRect();
    setThumb({ left: br.left - cr.left, width: br.width });
  }, [activeIndex, options.length]);

  return (
    <div
      ref={containerRef}
      className={`ui-seg-toggle ${disabled ? 'disabled' : ''} ${className ?? ''}`.trim()}
      role="radiogroup"
      aria-label={label}
    >
      <span
        className="ui-seg-toggle__thumb"
        aria-hidden="true"
        style={thumb
          ? { left: thumb.left, width: thumb.width, opacity: 1 }
          : { opacity: 0 }}
      />
      {options.map((opt) => (
        <button
          key={String(opt.value)}
          className={`ui-seg-toggle__option ${opt.value === value ? 'active' : ''}`}
          role="radio"
          aria-checked={opt.value === value}
          aria-label={opt['aria-label']}
          disabled={disabled}
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

interface SelectProps {
  children: ReactNode;
  value: string | number;
  onChange: (value: string) => void;
  className?: string;
  'aria-label'?: string;
}

/** Styled dropdown select */
export function Select({ children, value, onChange, className, 'aria-label': ariaLabel }: SelectProps) {
  return (
    <select
      className={`ui-select ${className ?? ''}`.trim()}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={ariaLabel}
    >
      {children}
    </select>
  );
}

interface InputProps {
  type?: string;
  value: string | number;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  min?: number;
  max?: number;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  'aria-label'?: string;
}

/** Styled text / number input */
export function Input({
  type = 'text',
  value,
  onChange,
  placeholder,
  className,
  min,
  max,
  onKeyDown,
  'aria-label': ariaLabel,
}: InputProps) {
  return (
    <input
      type={type}
      className={`ui-input ${className ?? ''}`.trim()}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      min={min}
      max={max}
      onKeyDown={onKeyDown}
      aria-label={ariaLabel}
    />
  );
}
